import React, { useMemo } from 'react'

import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { Stack, Typography, Box, InputAdornment, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { BoltOutlined, InfoRounded, LocalGasStationOutlined, MoreTimeOutlined } from '@mui/icons-material'

import { numericUtils } from '@/utils/numeric'
import { chargingUtils } from '@/utils/charging'
import { tariffUtils } from '@/utils/tariff'
import { timeUtils } from '@/utils/time'
import { TextInput } from '@/components/Inputs/Text'

import { ChargingStepProps, StartChargingStep } from '../hooks/useStartCharging/types'
import { ChargingStepActions } from '../components/ChargingStepActions'
import { hasSufficientWalletBalance } from '../helpers/has-sufficient-wallet-balance'
import { UseWalletBalanceInfo } from '../components/UseWalletBalanceInfo'
import { PopoverTooltip } from '@/components/Tooltip/PopoverTooltip'

interface AmountFormData {
  amount: string
  useWalletBalance: boolean
}

export const AmountStep: React.FC<ChargingStepProps> = ({ startChargingMethods }) => {
  const {
    state,
    chargingPoint,
    pendingBalance,
    minimumAmount,
    hasPendingBalance,
    hasPositiveBalance,
    setAmount,
    goBack,
    cancel,
    balance,
    setUseWalletBalance,
    setPaymentMethod,
    goToStep,
    shouldUseWalletBalanceOnly,
    chargingConfig,
    hasIdleRate,
  } = startChargingMethods

  const ratePerKwh = chargingPoint?.current_rate_per_kwh ?? chargingPoint?.rate_per_kwh ?? 0
  const activationFee = chargingPoint?.current_activation_fee ?? chargingPoint?.activation_fee ?? 0

  const idleRate = chargingPoint?.current_idle_rate ?? chargingPoint?.idle_rate ?? 0
  const idleGracePeriod = chargingPoint?.idle_grace_period_minutes ?? chargingConfig?.idle_grace_period_minutes
  const formattedIdleGrace = timeUtils.formatMinutesToTime(idleGracePeriod)
  const hasIdleSchedule = tariffUtils.hasVaryingRates(chargingPoint?.idle_timeline)

  const idleNoticeIntro = formattedIdleGrace
    ? `Passados ${formattedIdleGrace} do fim da carga,`
    : 'Assim que a carga terminar,'

  const formInitialValues: AmountFormData = useMemo(() => {
    return {
      amount: numericUtils.formatNumberToBRLDecimal(state.amount, { useGrouping: false, fallbackValue: '' }),
      useWalletBalance: state.useWalletBalance !== null ? state.useWalletBalance : hasPositiveBalance,
    }
  }, [state.amount, state.useWalletBalance, hasPositiveBalance])

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      amount: Yup.string()
        .required('Informe um valor')
        .test('is-valid-number', 'Informe um valor válido', (value) => {
          if (!value) return false
          const parsed = numericUtils.parseBRLDecimalToNumber(value)
          return !isNaN(parsed) && parsed > 0
        })
        .test(
          'is-above-minimum',
          `O valor deve ser maior ou igual a ${numericUtils.formatNumberToBRLCurrency(minimumAmount)}`,
          (value) => {
            if (!value) return false
            const parsed = numericUtils.parseBRLDecimalToNumber(value)
            if (isNaN(parsed)) return false
            return parsed >= minimumAmount
          }
        ),
    })
  }, [minimumAmount])

  const handleOnSubmit = (values: AmountFormData, { setSubmitting }: FormikHelpers<AmountFormData>) => {
    const parsedAmount = numericUtils.parseBRLDecimalToNumber(values.amount)

    if (!isNaN(parsedAmount) && parsedAmount >= minimumAmount) {
      setAmount(parsedAmount)
      setUseWalletBalance(values.useWalletBalance)

      if (shouldUseWalletBalanceOnly({ useWalletBalance: values.useWalletBalance, amount: parsedAmount, balance })) {
        setPaymentMethod(null)
      } else {
        goToStep(StartChargingStep.PAYMENT_METHOD)
      }
    }

    setSubmitting(false)
  }

  return (
    <Formik<AmountFormData>
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
      enableReinitialize
    >
      {({ values, submitForm, isSubmitting, isValid, setFieldValue }) => {
        const parsedAmount = numericUtils.parseBRLDecimalToNumber(values.amount)
        const amount = isNaN(parsedAmount) ? 0 : parsedAmount

        const chargingAmount = hasPendingBalance ? Math.max(0, amount - pendingBalance) : amount
        const equivalentKwh = chargingUtils.getEquivalentKwh(chargingAmount, ratePerKwh, activationFee)

        const hasValidAmount = values.amount !== '' && isValid
        const canContinue = hasValidAmount && !isSubmitting

        const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 7)
          if (digits === '') {
            setFieldValue('amount', '')
            return
          }
          const number = parseInt(digits, 10) / 100
          const formatted = numericUtils.formatNumberToBRLDecimal(number, { useGrouping: false, fallbackValue: '' })
          setFieldValue('amount', formatted)
        }

        const formattedMinimumAmount = numericUtils.formatNumberToBRLCurrency(minimumAmount)
        const formattedPendingBalance = numericUtils.formatNumberToBRLCurrency(pendingBalance)
        const formattedMinimumChargingValue = numericUtils.formatNumberToBRLCurrency(
          chargingUtils.getMinimumAmount(activationFee)
        )

        return (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocalGasStationOutlined color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Informar o valor
                </Typography>
              </Stack>
            </DialogTitle>

            <DialogContent>
              <Form>
                <Stack spacing={3}>
                  {hasPendingBalance ? (
                    <Box
                      sx={{
                        backgroundColor: 'warning.light',
                        borderRadius: 2,
                        padding: 2,
                      }}
                    >
                      <Typography variant="body2" color="warning.contrastText">
                        Devido a existência de saldo pendente, o valor mínimo para iniciar uma nova recarga é{' '}
                        <PopoverTooltip
                          content={`Esse valor se refere à soma entre o mínimo de recarga (incluindo ativação) e o saldo pendente de sessões anteriores
                            (${formattedMinimumChargingValue} + ${formattedPendingBalance}).`}
                        >
                          <strong>{formattedMinimumAmount}</strong>{' '}
                          <InfoRounded sx={{ color: 'white', fontSize: 12, ml: 0.5 }} />
                        </PopoverTooltip>
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Informe o valor que deseja abastecer no veículo. O mínimo é{' '}
                      <PopoverTooltip
                        content={`Esse valor se refere ao mínimo de recarga, que cobre a taxa de ativação vigente (${formattedMinimumChargingValue}).`}
                      >
                        <strong>{formattedMinimumAmount}</strong> <InfoRounded sx={{ fontSize: 12, ml: 0.5 }} />
                      </PopoverTooltip>
                      .
                    </Typography>
                  )}

                  <Stack gap={0.75}>
                    <Field
                      component={TextInput}
                      name="amount"
                      label="Valor"
                      placeholder="0,00"
                      fullWidth
                      onChange={handleAmountChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        inputProps: {
                          inputMode: 'numeric',
                        },
                      }}
                    />

                    {values.useWalletBalance && (
                      <Box
                        sx={{
                          backgroundColor: 'action.hover',
                          borderRadius: 2,
                          padding: 2,
                        }}
                      >
                        <UseWalletBalanceInfo amount={amount} balance={balance} />
                        {!hasSufficientWalletBalance(amount, balance) && (
                          <Stack
                            ml={5}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={0.5}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Valor a pagar:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {numericUtils.formatNumberToBRLCurrency(Number(amount) - Number(balance))}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    )}

                    <Box
                      sx={{
                        backgroundColor: 'action.hover',
                        borderRadius: 2,
                        padding: 2,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <BoltOutlined color="primary" sx={{ fontSize: 28 }} />
                        <Stack>
                          <Typography variant="body2" color="text.secondary">
                            Estimativa:
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="bold" color="primary">
                            {numericUtils.formatNumberToBRLDecimal(equivalentKwh)} kWh
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Valor: {numericUtils.formatNumberToBRLCurrency(ratePerKwh)}/kWh
                          </Typography>
                          {activationFee > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              inclui {numericUtils.formatNumberToBRLCurrency(activationFee)} de taxa de ativação
                            </Typography>
                          )}
                          {hasPendingBalance && chargingAmount < amount && (
                            <Typography variant="caption" color="warning.main">
                              {formattedPendingBalance} destinados ao saldo pendente de sessões anteriores
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </Box>

                    {hasIdleRate && (
                      <Stack direction="row" alignItems="flex-start" spacing={1.5} px={0.5}>
                        <MoreTimeOutlined sx={{ fontSize: 18, marginTop: 0.25, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          A taxa de ociosidade não está incluída nesse valor. {idleNoticeIntro} são cobrados{' '}
                          <Typography component="span" variant="caption" fontWeight="bold" color="primary">
                            {numericUtils.formatNumberToBRLCurrency(idleRate)}/min
                          </Typography>
                          {hasIdleSchedule ? ' (taxa vigente agora)' : ''} à parte, até o veículo ser retirado.
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Form>
            </DialogContent>

            <DialogActions>
              <ChargingStepActions sx={{ width: '100%' }}>
                <ChargingStepActions.ForwardButton onClick={submitForm} disabled={!canContinue} />
                <ChargingStepActions.BackwardButton onClick={hasPendingBalance ? goBack : cancel} />
              </ChargingStepActions>
            </DialogActions>
          </>
        )
      }}
    </Formik>
  )
}
